import { useEffect, useMemo, useState } from 'react';
import { MACHINE_PRESETS } from '../lab/catalog.js';
import { planRouting } from '../lab/router.js';
import { ApprovalQueue } from './components/ApprovalQueue.js';
import { BrowserSidecar } from './components/BrowserSidecar.js';
import { CodeSidecar } from './components/CodeSidecar.js';
import { LaneDetail } from './components/LaneDetail.js';
import { LaneGrid } from './components/LaneGrid.js';
import { SwarmFeed } from './components/SwarmFeed.js';
import { VoiceComposer } from './components/VoiceComposer.js';
import { WorkspaceRail } from './components/WorkspaceRail.js';
import {
  selectPendingApprovals,
  selectWorkspaceEvents,
  selectWorkspaceLanes,
  selectWorkspaceTabs,
  useMissionControlStore,
} from './store.js';
import { driveSwarm, planSwarm } from './swarm.js';

interface MissionControlProps {
  mode: 'beginner' | 'pro';
  onOpenLab: () => void;
}

export function MissionControl({ mode, onOpenLab }: MissionControlProps) {
  const workspaces = useMissionControlStore((s) => s.workspaces);
  const activeWorkspaceId = useMissionControlStore((s) => s.activeWorkspaceId);
  const lanes = useMissionControlStore((s) => selectWorkspaceLanes(s, s.activeWorkspaceId));
  const events = useMissionControlStore((s) => selectWorkspaceEvents(s, s.activeWorkspaceId));
  const approvals = useMissionControlStore((s) => selectPendingApprovals(s, s.activeWorkspaceId));
  const receipts = useMissionControlStore((s) =>
    s.receipts.filter((r) => r.workspaceId === s.activeWorkspaceId),
  );
  const tabs = useMissionControlStore((s) => selectWorkspaceTabs(s, s.activeWorkspaceId));
  const fileTree = useMissionControlStore((s) => s.fileTree);
  const selectedFilePath = useMissionControlStore((s) => s.selectedFilePath);
  const activeLaneId = useMissionControlStore((s) => s.activeLaneId);
  const activeLane = lanes.find((lane) => lane.id === activeLaneId) ?? null;

  const [sidecar, setSidecar] = useState<'browser' | 'code'>('browser');

  const activeWorkspace = workspaces.find((ws) => ws.id === activeWorkspaceId) ?? null;
  const activeGoal = activeWorkspace?.activeGoal ?? '';

  const routingHint = useMemo(() => {
    if (!activeWorkspace) return null;
    const machine = MACHINE_PRESETS[0];
    if (!machine) return null;
    const plan = planRouting(machine, { budget: 'standard' });
    const goalType = planSwarm(activeGoal).taskType;
    const route = plan.taskRoutes.find((r) => r.task === goalType);
    return route
      ? {
          decision: route.decision,
          rationale: route.rationale,
          localModel: route.localModel?.displayName,
          cloudModel: route.cloudModel?.displayName,
        }
      : null;
  }, [activeWorkspace, activeGoal]);

  useEffect(() => {
    if (!activeWorkspaceId || !activeGoal) return;
    const plan = planSwarm(activeGoal);

    const handle = driveSwarm(plan, {
      tickMs: 550,
      onLaneState: (role, status, note) => {
        const store = useMissionControlStore.getState();
        const laneId = store.lanes.find(
          (lane) => lane.workspaceId === activeWorkspaceId && lane.role === role,
        )?.id;
        if (!laneId) return;
        const nextStatus = status === 'running' ? 'running' : status === 'review' ? 'review' : 'done';
        store.setLaneStatus(laneId, nextStatus);
        store.appendLaneLog(laneId, {
          text: note,
          tone: status === 'done' ? 'good' : status === 'review' ? 'warn' : 'neutral',
        });
        store.emitEvent({
          workspaceId: activeWorkspaceId,
          laneId,
          kind: status === 'done' ? 'lane_done' : 'lane_progress',
          summary: note,
        });
      },
      onHandoff: (from, to, signal) => {
        const store = useMissionControlStore.getState();
        const toLane = store.lanes.find((lane) => lane.workspaceId === activeWorkspaceId && lane.role === to);
        store.emitEvent({
          workspaceId: activeWorkspaceId,
          laneId: toLane?.id,
          kind: 'lane_handoff',
          summary: `${from} → ${to}: ${signal}`,
        });
      },
      onApprovalRequest: (role, title) => {
        const store = useMissionControlStore.getState();
        const laneId = store.lanes.find(
          (lane) => lane.workspaceId === activeWorkspaceId && lane.role === role,
        )?.id;
        if (!laneId) return;
        store.requestApproval({
          workspaceId: activeWorkspaceId,
          laneId,
          title,
          risk: 'medium',
          summary: `${role} needs approval before writing.`,
          command: `# preview\n${title}`,
        });
        store.emitEvent({
          workspaceId: activeWorkspaceId,
          laneId,
          kind: 'approval_request',
          summary: title,
        });
      },
    });

    return () => handle.cancel();
  }, [activeWorkspaceId, activeGoal]);

  return (
    <div className="mission-control" data-mode={mode}>
      <div className="mission-control-rails">
        <aside className="mission-left">
          <WorkspaceRail workspaces={workspaces} />
          {routingHint ? (
            <section className="panel routing-hint" aria-label="Lab routing hint">
              <div className="section-heading">
                <span className="section-kicker">Router suggests</span>
                <strong>{routingHint.decision}</strong>
              </div>
              <p>{routingHint.rationale}</p>
              {routingHint.decision === 'local' ? (
                <p className="routing-detail">Local: {routingHint.localModel ?? '—'}</p>
              ) : routingHint.decision === 'cloud' ? (
                <p className="routing-detail">Cloud: {routingHint.cloudModel ?? '—'}</p>
              ) : (
                <p className="routing-detail">
                  Start local ({routingHint.localModel ?? '—'}), escalate to cloud (
                  {routingHint.cloudModel ?? '—'}).
                </p>
              )}
              <button type="button" className="ghost-button" onClick={onOpenLab}>
                Open routing plan
              </button>
            </section>
          ) : null}
        </aside>

        <main className="mission-main">
          <LaneGrid lanes={lanes} />
          <SwarmFeed events={events} />
          <VoiceComposer workspaceId={activeWorkspaceId} lanes={lanes} />
        </main>

        <aside className="mission-right">
          <LaneDetail lane={activeLane} siblings={lanes} />
          <ApprovalQueue approvals={approvals} receipts={receipts} />
          <section className="panel sidecar-switch" aria-label="Sidecar switch">
            <div className="sidecar-toggle" role="tablist" aria-label="Sidecar target">
              <button
                type="button"
                role="tab"
                aria-selected={sidecar === 'browser'}
                className={sidecar === 'browser' ? 'sidecar-toggle-active' : ''}
                onClick={() => setSidecar('browser')}
              >
                Browser
              </button>
              <button
                type="button"
                role="tab"
                aria-selected={sidecar === 'code'}
                className={sidecar === 'code' ? 'sidecar-toggle-active' : ''}
                onClick={() => setSidecar('code')}
              >
                Code
              </button>
            </div>
          </section>
          {sidecar === 'browser' ? (
            <BrowserSidecar workspaceId={activeWorkspaceId} tabs={tabs} lanes={lanes} />
          ) : (
            <CodeSidecar tree={fileTree} selectedPath={selectedFilePath} />
          )}
        </aside>
      </div>
    </div>
  );
}
